import React, { useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from 'Components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'Components/ui/popover';
import { cn } from 'lib/utils';
import TimePicker from './TimePicker';
import {
  DEFAULT_TIME, dateLabel, dayAllowed, firstAllowedSlot, joinValue, monthGrid, monthLabel, quickPicks,
  relativeLabel, riyadhToday, shiftMonth, slotAllowed, splitValue, timeLabel,
} from './scheduleView';

// When a push goes out, in Riyadh time: the usual evenings as one tap, or
// any day from a calendar and any minute from a clock. The value is a
// datetime-local string ("2026-11-06T20:00"), the same the page sends.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const NAV = 'h-8 w-8 p-0 shadow-none';

/** A month to pick a day from. Days before today and past the horizon can't be picked. */
const MonthCalendar = ({ month, onMonth, selected, now, onPick }) => {
  const today = riyadhToday(now);
  return (
    <div className='w-[280px]'>
      <div className='mb-2 flex items-center justify-between'>
        <Button type='button' variant='ghost' size='sm' className={NAV} aria-label='Previous month' onClick={() => onMonth(shiftMonth(month, -1))}>
          <ChevronLeft size={16} />
        </Button>
        <p className='text-sm font-medium text-foreground'>{monthLabel(month)}</p>
        <Button type='button' variant='ghost' size='sm' className={NAV} aria-label='Next month' onClick={() => onMonth(shiftMonth(month, 1))}>
          <ChevronRight size={16} />
        </Button>
      </div>
      <div className='grid grid-cols-7 text-center text-xs font-medium text-muted-foreground'>
        {WEEKDAYS.map((day, i) => (
          <span key={i} className='py-1'>{day}</span>
        ))}
      </div>
      {monthGrid(month).map((week) => (
        <div key={week[0].date} className='grid grid-cols-7'>
          {week.map(({ date, inMonth }) => {
            const allowed = dayAllowed(date, now);
            const isSelected = date === selected;
            return (
              <button
                key={date}
                type='button'
                disabled={!allowed}
                aria-pressed={isSelected}
                aria-label={dateLabel(date)}
                onClick={() => onPick(date)}
                className={cn(
                  'mx-auto my-0.5 h-8 w-8 appearance-none rounded-full border-[1px] border-solid border-transparent text-sm transition-colors',
                  isSelected
                    ? 'bg-foreground font-medium text-background'
                    : allowed
                    ? cn('bg-transparent hover:bg-muted', inMonth ? 'text-foreground' : 'text-muted-foreground')
                    : 'cursor-not-allowed bg-transparent text-muted-foreground/40',
                  date === today && !isSelected && 'border-foreground/50',
                )}
              >
                {Number(date.slice(8))}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const SchedulePicker = ({ value, onChange, disabled }) => {
  const now = Date.now();
  const { date, time } = splitValue(value);
  const [month, setMonth] = useState(() => `${(date || riyadhToday(now)).slice(0, 7)}-01`);
  const [open, setOpen] = useState(false);

  const pickDate = (next) => {
    // Keep the chosen time when it is still ahead on that day; else the next slot.
    onChange(joinValue(next, firstAllowedSlot(next, time || DEFAULT_TIME, now)));
    setOpen(false);
  };
  const pickTime = (next) => onChange(joinValue(date || riyadhToday(now), next));
  const allowed = !date || !time || slotAllowed(date, time, now);

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap gap-2'>
        {quickPicks(now).map((pick) => (
          <button
            key={pick.key}
            type='button'
            disabled={disabled}
            aria-pressed={value === pick.value}
            onClick={() => onChange(pick.value)}
            className={cn(
              'appearance-none rounded-full border-[1px] border-solid px-3.5 py-1.5 text-sm transition-colors disabled:opacity-50',
              value === pick.value
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-white text-foreground/80 hover:border-foreground/40 hover:text-foreground',
            )}
          >
            {pick.label}
          </button>
        ))}
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='outline'
              disabled={disabled}
              className={cn('h-10 justify-start bg-white font-normal shadow-none', !date && 'text-muted-foreground')}
            >
              <CalendarDays size={16} className='mr-2 text-muted-foreground' />
              {date ? dateLabel(date) : 'Pick a day'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align='start' className='w-auto p-3'>
            <MonthCalendar month={month} onMonth={setMonth} selected={date} now={now} onPick={pickDate} />
          </PopoverContent>
        </Popover>

        <TimePicker value={time} onChange={pickTime} disabled={disabled} allowed={allowed} />
        <span className='text-sm text-muted-foreground'>Riyadh time</span>
      </div>

      {date && time && allowed && (
        <p className='text-sm text-foreground'>
          Goes out <span className='font-medium'>{dateLabel(date)} at {timeLabel(time)}</span>
          <span className='text-muted-foreground'> · {relativeLabel(value, now)}</span>
        </p>
      )}
      {date && time && !allowed && (
        <p className='text-sm text-destructive'>That time has already passed. Pick a later one.</p>
      )}
    </div>
  );
};

export default SchedulePicker;
